import React from "react";
import { View, StyleSheet, ImageBackground } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { useAppConfig } from "../../../AppConfigProvider";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import CheckedBadge from "../../svgs/common/CheckedBadge";

const getStatusText = (progress) => {
  if (typeof progress === "number") {
    if (progress === 0) {
      return "Not Started";
    } else if (progress > 0 && progress < 100) {
      return "In Progress";
    } else if (progress >= 100) {
      return "Complete";
    }
  }
  return "Not Started";
};

const getButtonText = (progress) => {
  if (typeof progress === "number") {
    if (progress === 0) {
      return "Start Module";
    } else if (progress > 0 && progress < 100) {
      return "Resume Module";
    } else if (progress >= 100) {
      return "View Module";
    }
  }
  return "Start Module";
};

const getButtonStyleType = (progress) => {
  if (typeof progress === "number") {
    if (progress === 0) {
      return "primaryLight"; // Start Module
    } else if (progress > 0 && progress < 100) {
      return "primary"; // Resume Module
    } else if (progress >= 100) {
      return "primaryLight"; // View Module
    }
  }
  return "primaryLight";
};

function ModuleCatalogueCard({ module, onModulePress, isLoading, progress, cardWidth }) {
  const { primaryColor } = useAppConfig();

  const statusText = getStatusText(progress);
  const buttonText = getButtonText(progress);
  const buttonStyleType = getButtonStyleType(progress);
  const isComplete = progress >= 100;

  // Build button style dynamically
  const buttonStyle = [styles.actionButton];
  if (buttonStyleType === "primaryLight") {
    buttonStyle.push({
      borderWidth: 1,
      borderColor: "rgba(5, 6, 15, 0.10)",
    });
  }

  return (
    <View aria-label={`Module card ${module?.title}`} style={styles.container}>
      {module?.thumbnail ? (
        <ImageBackground
          source={{ uri: module.thumbnail }}
          style={styles.imageBackground}
          imageStyle={styles.backgroundImage}
          accessible={false}></ImageBackground>
      ) : null}

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.contentContainer}>
        <View style={styles.headerSection}>
          <CustomText
            text={module?.title || "Untitled Module"}
            size="l"
            weight="bold"
            style={styles.moduleTitle}
            useTranslationText={false}
            role="heading"
            aria-level="2"
          />
        </View>

        <View style={styles.progressSection}>
          <View style={styles.statusRow}>
            <View style={styles.statusLeft}>
              <CustomText
                text={statusText}
                size="s"
                weight="bold"
                style={styles.statusText}
                useTranslationText={false}
                role="heading"
                aria-level="3"
              />
            </View>
          </View>

          {typeof progress === "number" && (
            <>
              <CustomProgressBar
                progress={progress}
                color={isComplete ? "#22C55E" : primaryColor}
                backgroundColor="#E5E7EB"
                height={6}
                ariaLabel={`Module progress: ${Math.round(progress)}% complete`}
              />
            </>
          )}
        </View>

        <View style={styles.actionSection}>
          <CustomButton
            text={buttonText}
            onPress={() => onModulePress(module)}
            styleType={buttonStyleType}
            style={buttonStyle}
            textStyle={styles.buttonText}
            loading={isLoading}
            disabled={isLoading}
          />
          {isComplete && (
            <View style={styles.completeBadge}>
              <CheckedBadge accessible={true} accessibilityRole="image" accessibilityLabel="Complete badge" />
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
    maxWidth: 570,
    minWidth: 400,
    width: "100%",
    flexShrink: 0,
    // @ts-expect-error - Web CSS property
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  imageBackground: {
    width: "100%",
    height: 200,
  },
  backgroundImage: {
    borderTopLeftRadius: 23,
    borderTopRightRadius: 23,
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 24,
    paddingBottom: 24,
    backgroundColor: "white",
    flex: 1,
  },
  headerSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    width: "100%",
  },
  moduleTitle: {
    color: "#101828",
    paddingBottom: 20,
    paddingTop: 8,
  },
  progressSection: {
    width: "100%",
    paddingVertical: 20,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    gap: 12,
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  statusLeft: {
    flex: 1,
  },
  statusText: {
    color: "#667085",
  },
  actionSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  actionButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "600",
  },
  completeBadge: {
    marginLeft: 12,
  },
});

export default ModuleCatalogueCard;
